import { useState, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Bike, Plus, Pencil, FileText, Search, X, Loader2, Trash2 } from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatMoney } from '@/lib/api'
import { enterAdvance } from '@/hooks/useEnterAdvance'
import { useFilial } from '@/lib/filial'
import { enterAbrePrimeiro } from '@/lib/keyboard'

const CAMPOS_BIKE = ['aro', 'tamanho do quadro', 'suspensão', 'freio', 'marchas']

export default function EquipamentosPage() {
  const qc = useQueryClient()
  const [busca, setBusca] = useState('')
  const [editar, setEditar] = useState<any | null>(null)
  const [novo, setNovo] = useState(false)
  const [prontuario, setProntuario] = useState<number | null>(null)
  const filiais = useFilial(s => s.filiais)
  const filialAtualId = useFilial(s => s.filialAtual?.id ?? null)
  const nomeFilial = (fid: number | null | undefined) =>
    fid ? (filiais.find(f => f.id === fid)?.nome_fantasia || filiais.find(f => f.id === fid)?.razao_social || `Filial ${fid}`) : '—'

  const { data: lista = [], isLoading } = useQuery<any[]>({
    queryKey: ['equipamentos-cad', busca, filialAtualId],
    queryFn: async () => (await api.get('/servicos/equipamentos', { params: { q: busca.trim() || undefined } })).data,
  })

  // FIX (item #25 da lista, pedido do cliente): excluir equipamento/bike cadastrado.
  const excluir = useMutation({
    mutationFn: (id: number) => api.delete(`/servicos/equipamentos/${id}`),
    onSuccess: () => { toast.success('Equipamento excluído'); qc.invalidateQueries({ queryKey: ['equipamentos-cad'] }) },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao excluir'),
  })

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Bike className="text-brand-600" size={22} />
          <h1 className="text-xl font-bold text-ink-900">Equipamentos / Bikes</h1>
        </div>
        <button onClick={() => setNovo(true)} className="btn-primary flex items-center gap-1.5 !py-2">
          <Plus size={16} /> Nova bike
        </button>
      </div>
      <p className="text-sm text-ink-500 mb-4">Bikes dos clientes e o prontuário (histórico de OS) de cada uma.</p>

      <div className="relative max-w-sm mb-4">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
        <input value={busca} onChange={e => setBusca(e.target.value)}
          onKeyDown={enterAbrePrimeiro(lista, b => setEditar(b))}
          placeholder="Buscar por marca, modelo, série..."
          className="w-full pl-9 pr-3 py-2 text-sm bg-white border border-ink-200 rounded-lg focus:border-brand-400 focus:outline-none" />
      </div>

      {isLoading ? (
        <div className="flex justify-center py-10"><Loader2 className="animate-spin text-ink-400" /></div>
      ) : lista.length === 0 ? (
        <div className="text-center text-ink-400 py-10 bg-white rounded-xl border border-ink-200">Nenhuma bike cadastrada.</div>
      ) : (
        <div className="bg-white rounded-xl border border-ink-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-ink-50 text-ink-500 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-2">Bike</th>
                <th className="text-left px-4 py-2">Cliente</th>
                <th className="text-left px-4 py-2">Filial</th>
                <th className="text-left px-4 py-2">Nº série</th>
                <th className="px-4 py-2 w-24"></th>
              </tr>
            </thead>
            <tbody>
              {lista.map(b => (
                <tr key={b.id} className="border-t border-ink-100 hover:bg-ink-50">
                  <td className="px-4 py-2 font-medium">{b.descricao}{b.cor ? ` · ${b.cor}` : ''}</td>
                  <td className="px-4 py-2 text-ink-600">{b.cliente_nome}</td>
                  <td className="px-4 py-2 text-ink-600">{nomeFilial(b.filial_id)}</td>
                  <td className="px-4 py-2 font-mono text-ink-500">{b.numero_serie || '—'}</td>
                  <td className="px-4 py-2 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => setProntuario(b.id)} title="Prontuário" className="text-ink-400 hover:text-brand-600"><FileText size={15} /></button>
                      <button onClick={() => setEditar(b)} title="Editar" className="text-ink-400 hover:text-brand-600"><Pencil size={15} /></button>
                      <button
                        onClick={() => {
                          if (window.confirm(`Excluir "${b.descricao}"? Ela some das listas, mas o histórico de OS continua intacto.`)) {
                            excluir.mutate(b.id)
                          }
                        }}
                        disabled={excluir.isPending}
                        title="Excluir" className="text-ink-400 hover:text-rose-600 disabled:opacity-40">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(novo || editar) && (
        <ModalBike bike={editar} onClose={() => { setNovo(false); setEditar(null) }}
          onSalvo={() => { qc.invalidateQueries({ queryKey: ['equipamentos-cad'] }); setNovo(false); setEditar(null) }} />
      )}
      {prontuario && <ModalProntuario equipamentoId={prontuario} onClose={() => setProntuario(null)} />}
    </div>
  )
}

function ModalBike({ bike, onClose, onSalvo }: { bike: any | null; onClose: () => void; onSalvo: () => void }) {
  const isEdit = !!bike
  const [clienteId, setClienteId] = useState<number | null>(bike?.cliente_id || null)
  const [buscaCli, setBuscaCli] = useState(bike?.cliente_nome || '')
  const [abrirCli, setAbrirCli] = useState(false)
  const cliRef = useRef<HTMLDivElement>(null)
  const [marca, setMarca] = useState(bike?.marca || '')
  const [modelo, setModelo] = useState(bike?.modelo || '')
  const [ano, setAno] = useState(bike?.ano || '')
  const [cor, setCor] = useState(bike?.cor || '')
  const [numeroSerie, setNumeroSerie] = useState(bike?.numero_serie || '')
  const [extra, setExtra] = useState<Record<string, string>>(bike?.campos_custom || {})
  const filiais = useFilial(s => s.filiais)
  const filialAtual = useFilial(s => s.filialAtual)
  const [filialId, setFilialId] = useState<number | ''>(bike?.filial_id ?? filialAtual?.id ?? '')

  useEffect(() => {
    const onClick = (e: MouseEvent) => { if (cliRef.current && !cliRef.current.contains(e.target as Node)) setAbrirCli(false) }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])
  const { data: clientes = [] } = useQuery<any[]>({
    queryKey: ['bike-busca-cli', buscaCli],
    queryFn: async () => (await api.get('/cadastros/pessoas', { params: { tipo: 'cliente', q: buscaCli.trim() || undefined, limit: 20 } })).data,
    enabled: abrirCli && !isEdit,
  })

  const salvar = useMutation({
    mutationFn: () => {
      const body = {
        cliente_id: clienteId, tipo: 'bike',
        filial_id: filialId === '' ? null : Number(filialId),
        marca: marca || undefined, modelo: modelo || undefined, ano: ano || undefined,
        cor: cor || undefined, numero_serie: numeroSerie || undefined,
        campos_custom: Object.fromEntries(Object.entries(extra).filter(([, v]) => v)),
      }
      return isEdit ? api.put(`/servicos/equipamentos/${bike.id}`, body) : api.post('/servicos/equipamentos', body)
    },
    onSuccess: () => { toast.success(isEdit ? 'Bike atualizada' : 'Bike cadastrada'); onSalvo() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao salvar'),
  })

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg p-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-ink-900 flex items-center gap-2"><Bike size={18} className="text-brand-600" /> {isEdit ? 'Editar bike' : 'Nova bike'}</h2>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-600"><X size={20} /></button>
        </div>
        <div className="space-y-3" onKeyDown={enterAdvance}>
          {!isEdit && (
            <div className="relative" ref={cliRef}>
              <label className="text-xs font-semibold text-ink-500 uppercase">Cliente</label>
              <div className="relative mt-1">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
                <input value={buscaCli} placeholder="Digite o cliente..." autoComplete="off"
                  onChange={e => { setBuscaCli(e.target.value); setAbrirCli(true); if (clienteId) setClienteId(null) }}
                  onFocus={() => setAbrirCli(true)}
                  onKeyDown={e => { if (e.key === 'Enter' && clientes.length > 0) e.stopPropagation(); enterAbrePrimeiro(clientes, (c: any) => { setClienteId(c.id); setBuscaCli(c.nome); setAbrirCli(false) })(e) }}
                  className="w-full rounded-lg border border-ink-200 pl-9 pr-3 py-2 text-sm" />
              </div>
              {abrirCli && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg z-20 max-h-56 overflow-y-auto">
                  {clientes.length === 0 ? <div className="p-3 text-center text-sm text-ink-400">Nenhum cliente</div> :
                    clientes.map((c: any) => (
                      <button key={c.id} type="button" onClick={() => { setClienteId(c.id); setBuscaCli(c.nome); setAbrirCli(false) }}
                        className="w-full px-3 py-2 text-left border-b last:border-0 hover:bg-ink-50 text-sm">{c.nome}</button>
                    ))}
                </div>
              )}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><label className="text-xs font-semibold text-ink-500 uppercase">Filial</label>
              <select value={filialId} onChange={e => setFilialId(e.target.value === '' ? '' : Number(e.target.value))}
                className="mt-1 w-full rounded-lg border border-ink-200 px-3 py-2 text-sm bg-white">
                <option value="">— Sem filial —</option>
                {filiais.map(f => (
                  <option key={f.id} value={f.id}>{f.nome_fantasia || f.razao_social}</option>
                ))}
              </select></div>
            <div><label className="text-xs font-semibold text-ink-500 uppercase">Marca</label>
              <input value={marca} onChange={e => setMarca(e.target.value.toUpperCase())} className="mt-1 w-full rounded-lg border border-ink-200 px-3 py-2 text-sm" /></div>
            <div><label className="text-xs font-semibold text-ink-500 uppercase">Modelo</label>
              <input value={modelo} onChange={e => setModelo(e.target.value.toUpperCase())} className="mt-1 w-full rounded-lg border border-ink-200 px-3 py-2 text-sm" /></div>
            <div><label className="text-xs font-semibold text-ink-500 uppercase">Ano</label>
              <input value={ano} onChange={e => setAno(e.target.value)} className="mt-1 w-full rounded-lg border border-ink-200 px-3 py-2 text-sm" /></div>
            <div><label className="text-xs font-semibold text-ink-500 uppercase">Cor</label>
              <input value={cor} onChange={e => setCor(e.target.value.toUpperCase())} className="mt-1 w-full rounded-lg border border-ink-200 px-3 py-2 text-sm" /></div>
            <div className="col-span-2"><label className="text-xs font-semibold text-ink-500 uppercase">Nº de série / quadro</label>
              <input value={numeroSerie} onChange={e => setNumeroSerie(e.target.value.toUpperCase())} className="mt-1 w-full rounded-lg border border-ink-200 px-3 py-2 text-sm" /></div>
            {CAMPOS_BIKE.map(c => (
              <div key={c}><label className="text-xs font-semibold text-ink-500 uppercase">{c}</label>
                <input value={extra[c] || ''} onChange={e => setExtra({ ...extra, [c]: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-ink-200 px-3 py-2 text-sm" /></div>
            ))}
          </div>
          <button tabIndex={0} onClick={() => { if (!clienteId) { toast.error('Selecione o cliente'); return } salvar.mutate() }}
            disabled={salvar.isPending} className="w-full btn-primary disabled:opacity-50">
            {salvar.isPending ? 'Salvando...' : (isEdit ? 'Salvar' : 'Cadastrar bike')}
          </button>
        </div>
      </div>
    </div>
  )
}

function ModalProntuario({ equipamentoId, onClose }: { equipamentoId: number; onClose: () => void }) {
  const { data, isLoading } = useQuery<any>({
    queryKey: ['prontuario-cad', equipamentoId],
    queryFn: async () => (await api.get(`/servicos/equipamentos/${equipamentoId}/prontuario`)).data,
  })
  const e = data?.equipamento
  const hist = data?.historico || []
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[85vh] flex flex-col" onClick={ev => ev.stopPropagation()}>
        <div className="px-5 py-4 border-b border-ink-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-ink-900 flex items-center gap-2"><FileText size={18} className="text-brand-600" /> Prontuário</h2>
            <button onClick={onClose} className="text-ink-400 hover:text-ink-600"><X size={20} /></button>
          </div>
          {e && (
            <div className="mt-1">
              <div className="font-semibold text-ink-900">{e.descricao}{e.cor ? ` · ${e.cor}` : ''}</div>
              <div className="text-xs text-ink-500">
                {e.numero_serie ? `nº ${e.numero_serie}` : ''}
                {Object.entries(e.campos_custom || {}).map(([k, v]) => ` · ${k}: ${v}`).join('')}
              </div>
            </div>
          )}
        </div>
        <div className="p-4 overflow-y-auto">
          {isLoading ? <div className="text-center text-ink-400 py-8">Carregando...</div>
            : hist.length === 0 ? <div className="text-center text-ink-400 py-8">Nenhum serviço no histórico ainda.</div>
            : (
              <div className="space-y-2">
                {hist.map((h: any) => (
                  <div key={h.os_id} className="border border-ink-200 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-sm">OS #{h.numero}</span>
                      <span className="text-sm font-bold text-emerald-700">{formatMoney(h.valor_total)}</span>
                    </div>
                    <div className="text-xs text-ink-500">{h.data_abertura ? new Date(h.data_abertura).toLocaleDateString('pt-BR') : ''} · {h.status}</div>
                    {h.servicos.length > 0 && <div className="text-xs text-ink-700 mt-1">{h.servicos.join(', ')}</div>}
                    {h.descricao_problema && <div className="text-[11px] text-ink-500 mt-0.5 italic">"{h.descricao_problema}"</div>}
                  </div>
                ))}
              </div>
            )}
        </div>
      </div>
    </div>
  )
}
